import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const LOADING_LABEL = 'Loading…';
export const LOAD_FAILED_MESSAGE = "Couldn't load this. Check your connection and try again.";

/** One placeholder bar. Size and shape come from `className`. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn('animate-pulse rounded-md bg-muted', className)} />;
}

/**
 * Placeholder rows shaped like a list that is still loading, so the real rows
 * replace them without the layout jumping. `renderRow` draws one row; the
 * default is a single text bar.
 */
export function SkeletonList({
  rows = 3,
  className,
  rowClassName,
  renderRow,
  label = LOADING_LABEL,
}: {
  rows?: number;
  className?: string;
  rowClassName?: string;
  renderRow?: (index: number) => ReactNode;
  label?: string;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={className}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className={rowClassName}>
          {renderRow ? renderRow(index) : <Skeleton className="h-4 w-full" />}
        </div>
      ))}
    </div>
  );
}

/** A failed load: a short message and a Retry button. */
export function LoadError({
  message = LOAD_FAILED_MESSAGE,
  onRetry,
  className,
  compact = false,
}: {
  message?: string;
  onRetry?: () => void;
  className?: string;
  /** Inline variant for menus and narrow panels. */
  compact?: boolean;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-2 text-sm text-muted-foreground',
        compact ? 'px-2 py-1.5 text-xs' : 'flex-col justify-center px-4 py-8 text-center sm:flex-row',
        className,
      )}
    >
      <span className="inline-flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
        <span className="min-w-0">{message}</span>
      </span>
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          className={cn(compact ? 'ml-auto h-7 px-2 text-xs' : 'h-8')}
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
}
